import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import type { HTMLChakraProps } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';

import {
  Badge,
  getTextContainsHighlight,
  TruncateTextWithTooltip,
} from '@frontend/components';

import {
  applyHighlighting,
  getHighlightQueryStringForConstituent,
  usePartContext,
} from './util';

export type PartLabelProps = HTMLChakraProps<'span'>;

export const PartLabel = ({ className, ...props }: PartLabelProps) => {
  const { number, filename, rev, isItar, highlight, size, variant, truncate } =
    usePartContext();
  const styles = useMultiStyleConfig('Part', { size, variant });

  const highlightedNumber = applyHighlighting({
    contentToHighlight: number,
    constituent: 'number',
    highlight,
  });
  const highlightedFilename = applyHighlighting({
    contentToHighlight: filename,
    constituent: 'filename',
    highlight,
  });
  const highlightedRev = applyHighlighting({
    contentToHighlight: rev,
    constituent: 'rev',
    highlight,
  });

  const filenameHighlightQueryString = getHighlightQueryStringForConstituent({
    constituent: 'filename',
    highlight,
  });

  const filenameIsHighlighted =
    !!filename &&
    !!filenameHighlightQueryString &&
    getTextContainsHighlight({
      text: filename,
      query: [filenameHighlightQueryString],
    });

  const _className = cx('part__label-container', className);

  return (
    <chakra.span
      __css={{
        ...styles.labelContainer,
        ...(truncate && {
          display: 'grid',
          gridTemplateColumns: `repeat(${rev ? 3 : 1}, minmax(0, min-content))`,
          alignItems: 'baseline',
        }),
      }}
      {...props}
      className={_className}
    >
      {/* Primary text: number (with filename fallback) or filename-only in truncate */}
      {truncate ? (
        number || rev ? (
          <TruncateTextWithTooltip
            as="span"
            text={number || '--'}
            displayText={number || '--'}
            fontSize="inherit"
            fontWeight="inherit"
            color={number ? 'inherit' : 'feature.placeholder'}
            fontFamily="inherit"
            lineHeight="inherit"
            minWidth={0}
            mr="0.5ch"
            maxWidth="100%"
            tooltipProps={{ label: number ?? '--' }}
            __css={number ? styles.label : styles.labelPlaceholder}
          />
        ) : (
          <TruncateTextWithTooltip
            as="span"
            text={filename || '--'}
            displayText={filename || '--'}
            fontSize="inherit"
            fontWeight="inherit"
            color={filename ? 'inherit' : 'feature.placeholder'}
            fontFamily="inherit"
            lineHeight="inherit"
            minWidth={0}
            mr="0.5ch"
            __css={filename ? styles.label : styles.labelPlaceholder}
          />
        )
      ) : highlightedNumber || highlightedFilename ? (
        <chakra.span className="part__label" __css={styles.label}>
          {highlightedNumber || highlightedFilename}
        </chakra.span>
      ) : (
        <chakra.span
          className="part__label-placeholder"
          __css={styles.labelPlaceholder}
        >
          --
        </chakra.span>
      )}

      {/* Rev prefix + value */}
      {rev &&
        (truncate ? (
          <>
            <chakra.span
              __css={styles.labelPrefix}
              flexShrink={0}
              fontFamily="data"
            >
              rev
            </chakra.span>
            <TruncateTextWithTooltip
              as="span"
              text={rev}
              displayText={rev}
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
              fontFamily="inherit"
              lineHeight="inherit"
              minWidth={0}
              maxWidth="4ch"
              flexShrink={0}
              __css={styles.label}
            />
          </>
        ) : (
          <chakra.span className="part__label-group" __css={styles.labelGroup}>
            <chakra.span
              className="part__label-prefix"
              __css={styles.labelPrefix}
            >
              rev
            </chakra.span>
            <chakra.span className="part__label" __css={styles.label}>
              {highlightedRev}
            </chakra.span>
          </chakra.span>
        ))}

      {/* Secondary filename shown when highlighted but not displayed above */}
      {!truncate &&
        highlightedNumber &&
        highlightedFilename &&
        filenameIsHighlighted && (
          <chakra.div
            className="part__secondary-label"
            __css={styles.secondaryLabel}
          >
            {highlightedFilename}
          </chakra.div>
        )}

      {isItar && (
        <chakra.div
          className="part__badge-container"
          __css={styles.badgeContainer}
        >
          <Badge
            size={size === 'xl' || size === '2xl' ? 'sm' : 'xs'}
            bg="tag.ITAR"
            borderColor="tag.ITAR"
            color="white"
          >
            ITAR
          </Badge>
        </chakra.div>
      )}
    </chakra.span>
  );
};
